import { z } from 'zod';
import { CustomElementsEnum } from '@trinity/custom-pages-registry-types';
import { workflowStepSchema } from './workflow-steps.js';

export const iconnectSyncMappingSchema = z.object({
  nonTextareaMap: z.record(z.string(), z.string()),
  textareaKeys: z.array(z.string()),
  allSurveyQuestions: z.record(z.string(), z.string()),
  surveyTargetRecordType: z.string().optional(),
  questionResponseRecordType: z.string().optional(),
  surveyTargetFields: z.record(z.string(), z.string()).optional(),
});

export const iconnectSyncConfigSchema = z.object({
  enabled: z.boolean().prefault(false),
  syncToIconnectButtonText: z.string().optional(),
  surveyName: z.string().optional(),
  mapping: iconnectSyncMappingSchema.optional(),
});

export const workflowIntroConfigSchema = z.object({
  warningMessage: z.string(),
  showSpeechOption: z.boolean(),
  showManualButton: z.boolean(),
  showFileUploadOption: z.boolean().prefault(false),
  showCancelButton: z.boolean(),
  extractionPrompt: z.string().optional(),
  disclaimerEnabled: z.boolean().prefault(false),
  disclaimerTitle: z.string().optional(),
  disclaimerText: z.string().optional(),
  submitButtonText: z.string().optional(),
  showPrefilledMessage: z.boolean().optional(),
  prefilledMessage: z.string().optional(),
  iconnectSync: iconnectSyncConfigSchema.optional(),
  introResponse: z
    .object({
      value: z.string(),
      attachments: z.array(z.unknown()).optional(),
    })
    .optional(),
  dashboardElement: CustomElementsEnum.nullable().optional(),
});

export const formWorkflowRegistrySchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  features: z.array(z.string()),
  examples: z.array(z.string()),
  createdOn: z.coerce.date(),
  updatedOn: z.coerce.date(),
  createdBy: z.number().nullable().optional(),
  // The workflow's UUID, which is how everything names it.
  identifier: z.uuid(),
  // Null until the workflow is given a brand.
  brandId: z.number().nullable().optional(),
  // The same brand by identifier, which chat-api compares with the brand a chat runs in. Null when the workflow
  // has no brand; absent when the response did not carry it.
  brandIdentifier: z.string().nullable().optional(),
  // Identifies the intent a workflow serves. A workflow split between brands keeps one lineage across its copies.
  lineageId: z.string().optional(),
  // The forms the workflow runs, in order. Absent from a server that predates steps, and empty for a workflow that has
  // none yet, which then runs nothing.
  steps: z.array(workflowStepSchema).optional(),
});

// Lists every workflow, or only the ones of one brand.
export const listFormWorkflowsQuerySchema = z.object({
  brandId: z.coerce.number().int().positive().optional(),
});

export const listFormWorkflowsResponseSchema = z.object({
  items: z.array(formWorkflowRegistrySchema),
});

export const getFormWorkflowResponseSchema = z.object({
  item: formWorkflowRegistrySchema,
});

// A step as it is sent: only the form it runs, by the form's identifier. Its position is its place in the list. Strict,
// so a condition or a position cannot be sent before the API supports them.
export const workflowStepInputSchema = z.strictObject({
  formId: z.string().min(1),
});

// Replaces the workflow's steps. Today a workflow has at most one, and an empty list removes it.
export const updateWorkflowStepsBodySchema = z.strictObject({
  steps: z.array(workflowStepInputSchema),
});

// Strict: the tool wording, the settings and the DAG a workflow used to carry are gone, and a request that still sends one
// is refused with the key named, rather than quietly ignored.
export const createFormWorkflowBodySchema = z.strictObject({
  // Given when a workflow is cloned into another environment, so that references to it still resolve there. Left out, the
  // workflow gets a new one. An identifier already in use is refused.
  identifier: z.uuid().optional(),
  name: z.string().min(1),
  brandId: z.number().int().positive(),
  description: z.string().optional(),
  features: z.array(z.string()).optional(),
  examples: z.array(z.string()).optional(),
  steps: z.array(workflowStepInputSchema).optional(),
});

export const updateFormWorkflowBodySchema = z.strictObject({
  name: z.string().min(1).optional(),
  // Can be set on a workflow that has no brand, and changed while no form is attached to the workflow.
  brandId: z.number().int().positive().optional(),
  description: z.string().optional(),
  features: z.array(z.string()).optional(),
  examples: z.array(z.string()).optional(),
});

export function isWorkflowIdentifier(ref: string): boolean {
  return z.uuid().safeParse(ref).success;
}

export type FormWorkflowLookup = { workflowIdentifier: string };

// The workflow's numeric id or its identifier.
export const formWorkflowByIdParamsSchema = z.object({
  workflowId: z.union([z.uuid(), z.coerce.number()]),
});

export type WorkflowStepInput = z.infer<typeof workflowStepInputSchema>;
export type UpdateWorkflowStepsBody = z.infer<typeof updateWorkflowStepsBodySchema>;
export type WorkflowIntroConfig = z.infer<typeof workflowIntroConfigSchema>;
export type IconnectSyncMapping = z.infer<typeof iconnectSyncMappingSchema>;
export type IconnectSyncConfig = z.infer<typeof iconnectSyncConfigSchema>;
export type FormWorkflowRegistry = z.infer<typeof formWorkflowRegistrySchema>;
export type ListFormWorkflowsQuery = z.infer<typeof listFormWorkflowsQuerySchema>;
export type ListFormWorkflowsResponse = z.infer<typeof listFormWorkflowsResponseSchema>;
export type GetFormWorkflowResponse = z.infer<typeof getFormWorkflowResponseSchema>;
export type CreateFormWorkflowBody = z.infer<typeof createFormWorkflowBodySchema>;
export type UpdateFormWorkflowBody = z.infer<typeof updateFormWorkflowBodySchema>;

export interface WorkflowConfig {
  /** The workflow's UUID, which is how everything names it. */
  identifier: string;
  /** The brand the workflow belongs to. Null when it has none yet; absent when the server did not say. */
  brandIdentifier?: string | null;
  /** Null only when the workflow is confirmed to have no brand. Absent means the owner cannot be told. */
  brandId?: number | null;
  /** Shared by a workflow and the copies it was split into, one per brand. */
  lineageId?: string;
  name: string;
  /** What the workflow is for, which is what chat tells the model about it. Absent or blank means the name does. */
  description?: string | null;
}

// Alias for backward compatibility
export type WorkflowType = string;

export const formWorkflowSubmissionDataSchema = z.object({
  queryId: z.string(),
  workflowType: z.string(),
  formParentId: z.number(),
  formVersion: z.string(),
  formData: z.array(
    z.object({
      fieldLabel: z.string(),
      value: z.unknown(),
    })
  ),
  aiState: z
    .object({
      originalExtraction: z.record(z.string(), z.unknown()).optional(),
      userTranscript: z.string().optional(),
      formParentId: z.number().optional(),
      formVersion: z.string().optional(),
    })
    .optional(),
  userId: z.number(),
  brandIdentifier: z.string(),
  /** The revision of the draft the form card was built from. A card built before revisions existed names none. */
  revision: z.number().int().min(0).optional(),
});

export const formWorkflowStateUpdateDataSchema = z.object({
  queryId: z.string(),
  workflowType: z.string(),
  formState: z.string().optional(),
  formData: z.array(
    z.object({
      fieldLabel: z.string(),
      value: z.unknown(),
    })
  ),
  /** The revision of the draft the form card was built from. A card built before revisions existed names none. */
  revision: z.number().int().min(0).optional(),
});

export const workflowMatchResultSchema = z.object({
  workflow: z.object({
    identifier: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
  }),
  confidence: z.number(),
  reasoning: z.string(),
});

export type FormWorkflowSubmissionData = z.infer<typeof formWorkflowSubmissionDataSchema>;
export type FormWorkflowStateUpdateData = z.infer<typeof formWorkflowStateUpdateDataSchema>;
export type WorkflowMatchResult = z.infer<typeof workflowMatchResultSchema>;

